import {createSlice} from '@reduxjs/toolkit';
import {FeatureSelectionUtility} from "../utilities/FeatureSelectionUtility";
import {TranslationUtility} from "../utilities/TranslationUtility";
import {CameraShotUtility} from "../utilities/CameraShotUtility";
import {RulesUtility} from "../utilities/RulesUtility";
import {ApplicationState, CategoryType, Language, PopupState, RequestBudgetStatus, UserStatus} from "./constants";
import ConfigurationMock from './base-data';
import IsNullOrEmpty from "../utils/StringUtils";
import IsNullOrUndefined from "../utils/ObjectUtils";
import {ImprovementRulesUtility} from "../utilities/ImprovementRulesUtility";

const initialState = {
  applicationState: ApplicationState.NotInitialized,
  popupState: PopupState.None,
  settings: null,
  user: null,
  userLoginError: null,
  unitUsers: null,
  legalSettings: ConfigurationMock.legalSettings,
  rooms: ConfigurationMock.rooms,
  categories: ConfigurationMock.categories,
  rules: ConfigurationMock.rules,
  prices: [],
  promotionSlug: '',
  reviewSlug: '',
  currentUnit: null,
  totalPrice: 0,
  vatPercentage: 10,
  isLoading: true,
  mainCategoryAddedFromRule: null,
  mainCategoryRemovedFromRule: null,
  addedCategoriesFromRule: [],
  removedCategoriesFromRule: [],
  currentRoom: null,
  currentCamera: null,
  currentShot: null,
  currentShots: [],
  isGeneralShot: false,
  currentCameras: [],
  focusedCategory: null,
  isDescriptionOpened: false,
  isMainMenuOpened: false,
  isSummaryOpened: false,
  categoryForDescriptionPanel: null,
  featureForDescriptionPanel: null,
  isCategoryFocusEnabled: true,
  sections: ConfigurationMock.sections,
  summaryCameras: ConfigurationMock.summaryCameras,
  summaryShots: null,
  printingCameras: [],
  printingShots: null,
  isCategoriesUnfolded: true,
  currentUnfoldedCategory: "",
  currentUnfoldOffset: 0,
  isSavingConfiguration: false,
  isDownloadingPdf: false,
  isRequestingBudget: false,
  requestingBudgetUnitId: null,
  currentConfigCode: "",
  configurationChanged: false,
  snackbarMessage: "",
  currentLanguage: Language.es,
  translations: [],
  currentTranslations: {}
};

const calculateTotalPrice = (categories) => {
  let total = 0;
  categories.forEach((category) => {
    const currentFeature = category.features.find(feature => feature.id === category.selected_feature);
    if (!IsNullOrUndefined(currentFeature)) {
      const featurePrice = parseFloat(currentFeature.price);

      if (!isNaN(featurePrice)) {
        total += featurePrice;
      }
    }
  });

  return total;
};

export const configuratorSlice = createSlice({
  name: 'configurator',
  initialState,
  reducers: {
    setPromotionId: (state, action) => {
      state.promotionId = action.payload;
    },
    initializePromotion: (state, action) => {
      state.settings = action.payload.settings;
      state.categories = action.payload.categories;
      // state.sections = action.payload.sections;
      state.rules = action.payload.rules;
      state.improvementRules = action.payload.improvementRules;
      // state.translations = action.payload.translations;
      state.isLoading = false;
      state.currentShot = null;
      state.currentShots = [];
      state.currentConfigCode = '';
      state.currentUnfoldedCategory = "";
      state.currentUnfoldOffset = 0;
      state.configurationChanged = false;
      state.totalPrice = 0;

      let tempRooms = [];
      action.payload.rooms.forEach((room) => {
        let foundEnabledCategory = false;
        room.categories.forEach((roomCategory) => {
          const foundCategory = action.payload.categories.find((c) => c.id === roomCategory.id);
          if (!IsNullOrUndefined(foundCategory) && foundCategory.disabled) {

          } else {
            foundEnabledCategory = true;
          }
        });

        if (foundEnabledCategory) {
          tempRooms.push(room);
        }

        state.rooms = tempRooms;
      });

      RulesUtility.validateAllRules(state.rules, state.categories);
      ImprovementRulesUtility.applyImprovementRules(state.improvementRules, state.rules, state.categories);

      state.applicationState = ApplicationState.PromotionLoaded;

    },
    resetPromotion: (state, action) => {
      state.isLoading = true;
      state.currentConfigCode = '';
      state.currentUnit = null;
    },
    setApplicationState: (state, action) => {
      state.applicationState = action.payload;
    },
    setPopupState: (state, action) => {
      state.popupState = action.payload;
    },
    applyFeatureSelection: (state, action) => {
      state.categories = FeatureSelectionUtility.ApplyFeatureSelection(state.categories, action.payload);
    },
    changeRoom: (state, action) => {
      state.rooms.forEach(room => {
        if(room.id === action.payload)
        {
          const previousShot = state.currentShot;
          const previousShots = state.currentShots;
          const previousCameras = state.currentCameras;

          state.currentRoom = room;
          state.focusedCategory = null;

          state.currentCamera = CameraShotUtility.findBestCameraForCategory(state.focusedCategory, state.currentRoom, state.currentCamera, false);
          state.previousShot = state.currentShot;
          state.currentCameras = state.currentRoom.cameras;
          state.currentShots = CameraShotUtility.updateCameraShots(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
          state.currentShot = CameraShotUtility.getCameraShot(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);

          state.isGeneralShot = state.currentShots.length === 0;

          if(state.currentShots.length === 0){
            state.currentShots = previousShots;
          }

          if(state.currentShot === undefined){
            state.currentShot = previousShot;
          }
          if(state.currentCameras.length === 0){
            state.currentCameras = previousCameras;
          }
        }
      });
    },
    changeFeature: (state, action) => {
      if(action.payload.skipValidation !== true && !RulesUtility.validateRulesForSelectedFeature(
        state.rules,
        state.categories,
        state.addedCategoriesFromRule,
        state.removedCategoriesFromRule,
        action.payload.category,
        action.payload.feature))
      {
        state.mainCategoryAddedFromRule = {
          ...state.categories.find(category => category.id === action.payload.category)
        };
        state.mainCategoryAddedFromRule.selected_feature = action.payload.feature;
        state.mainCategoryRemovedFromRule = {
          ...state.categories.find(category => category.id === action.payload.category)
        };
        state.popupState = PopupState.Rules;
        return;
      }

      state.categories.forEach(category => {
        if (category.id === action.payload.category) {
          if (category.look_type === CategoryType.Improvement && action.payload.hasFeatureBeenUnselected) {
            category.selected_feature = 0;
          } else {
            category.selected_feature = action.payload.feature;
          }
          state.focusedCategory = category;
          state.rooms.map(room => {
            if (room.id === action.payload.room) {
              state.currentRoom = room;
            }
          });
        }
      });

      ImprovementRulesUtility.applyImprovementRules(state.improvementRules, state.rules, state.categories);

      state.categories.forEach(category => {
        if (category.id === action.payload.category) {

          const camera = CameraShotUtility.findBestCameraForCategory(state.focusedCategory, state.currentRoom, state.currentCamera, true);
          if (!IsNullOrUndefined(camera) && !IsNullOrUndefined(camera.id)) {
            state.currentCamera = camera;
            state.previousShot = state.currentShot;

            const previousShot = state.currentShot;
            const previousShots = state.currentShots;
            const previousCameras = state.currentCameras;

            state.currentCameras = state.currentRoom.cameras;
            state.currentShots = CameraShotUtility.updateCameraShots(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
            state.currentShot = CameraShotUtility.getCameraShot(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
            state.configurationChanged = true;

            state.isGeneralShot = state.currentShots.length === 0;

            if(state.currentShots.length === 0){
              state.currentShots = previousShots;
            }

            if(state.currentShot === undefined){
              state.currentShot = previousShot;
            }
            if(state.currentCameras.length === 0){
              state.currentCameras = previousCameras;
            }
          } else {
            state.previousShot = state.currentShot;
            state.currentShots = CameraShotUtility.updateCameraShots(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
            state.currentShot = CameraShotUtility.getCameraShot(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
          }

          let feature = category.features.find(f => f.id === action.payload.feature);
          if (CameraShotUtility.IsIconOnly(state.rooms, state.improvementRules, category, feature)) {
            state.categoryForDescriptionPanel = category;
            state.featureForDescriptionPanel = feature;
            state.isDescriptionOpened = true;
          }

          state.totalPrice = calculateTotalPrice(state.categories);
        }
      });
    },
    updateTotalPrice: (state, action) => {
      state.totalPrice = calculateTotalPrice(state.categories);
    },
    changeCategoriesUnfolded: (state, action) => {
      state.isCategoriesUnfolded = action.payload;
    },
    changeIsDescriptionOpened: (state, action) => {
      if(action.payload.categoryId !== undefined && action.payload.featureId !== undefined)
      {
        state.categoryForDescriptionPanel = state.categories.find(c => c.id === action.payload.categoryId);
        state.featureForDescriptionPanel = state.categoryForDescriptionPanel.features.find(f => f.id === action.payload.featureId);
      }

      state.isDescriptionOpened = action.payload.isOpen;
    },
    changeIsMainMenuOpened: (state, action) => {
      state.isMainMenuOpened = action.payload;
    },
    changeIsSummaryOpened: (state, action) => {
      state.isSummaryOpened = action.payload;
      state.summaryShots = [];
      if(state.isSummaryOpened)
      {
        state.summaryCameras.forEach(summaryCamera => {
          state.rooms.forEach(room => {
            const foundCamera = room.cameras.filter(camera => camera.id === summaryCamera.id)[0] || null;

            if (foundCamera !== null) {
              let shot = CameraShotUtility.getCameraShot(state.settings.project_id, room, foundCamera, state.categories);
              state.summaryShots.push(shot);
            }
          });
        });
      }
    },
    changeIsCategoryFocusEnabled: (state, action) => {
      state.isCategoryFocusEnabled = action.payload;
    },
    updateFocusedCategory: (state, action) => {
      if(state.isCategoryFocusEnabled)
      {
        state.categories.map(category => {
          if (category.id === action.payload.category) {
            state.focusedCategory = category;
            state.rooms.map(room => {
              if (room.id === action.payload.room) {
                state.currentRoom = room;
              }
            });

            state.currentCamera = CameraShotUtility.findBestCameraForCategory(state.focusedCategory, state.currentRoom, state.currentCamera, false);
            state.previousShot = state.currentShot;
            state.currentCameras = state.currentRoom.cameras;
            state.currentShots = CameraShotUtility.updateCameraShots(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);
            state.currentShot = CameraShotUtility.getCameraShot(state.settings.project_id, state.currentRoom, state.currentCamera, state.categories);

            state.isGeneralShot = state.currentShots.length === 0;
          }
        });
      }
    },
    updatePreviousShot: (state, action) => {
      state.previousShot = action.payload;
    },
    updateCurrentCameraShot: (state, action) => {
      state.currentCamera = action.payload.camera;
      state.currentShot = action.payload.shot;
    },
    initializeSummaryCameras: (state, action) => {
      state.summaryCameras = [];
      state.rooms.forEach(room => {
        room.cameras.forEach(camera => {
          if (camera.is_summary){
            state.summaryCameras.push(camera);
          }
        });
      });
    },
    initializeSections: (state, action) => {
      state.sections.map(section => {
        let filledCategories = [];
        section.categories.forEach(sectionCategory => {
          state.categories.forEach(category => {
            if(category.id === sectionCategory)
            {
              filledCategories.push(category);
            }
          });
        });
        section.categories = filledCategories;
      });
    },
    updateSections: (state, action) => {
      state.sections.map(section => {
        let filledCategories = [];
        section.categories.forEach(sectionCategory => {
          state.categories.forEach(category => {
            if(category.id === sectionCategory.id)
            {
              filledCategories.push(category);
            }
          });
        });
        section.categories = filledCategories;
      });
    },
    initializeFeatureSelectionForPrinting: (state, action) => {
      FeatureSelectionUtility.ApplyFeatureSelectionString(state.categories, action.payload);
      ImprovementRulesUtility.applyImprovementRules(state.improvementRules, state.rules, state.categories);
    },
    initializeSectionsForPrinting: (state) => {

      /* state.rooms.map(room => {
        let filledCategories = [];
        room.categories.forEach(sectionCategory => {
          state.categories.forEach(category => {
            if(category.id === sectionCategory)
            {
              filledCategories.push(category);
            }
          });
        });
        //room.categories = filledCategories;
      }); */

      state.printingShots = [];

      const allFirstCameras = [];
      state.rooms.forEach((room) => {
        room.cameras.forEach((camera) => {
          if (camera.name.endsWith("1")) {
            allFirstCameras.push({
              id: camera.id,
              name: camera.name,
              roomId: room.id
            });
          }
        });
      });

      console.log(allFirstCameras);


      allFirstCameras.map(camera => {
        const room = state.rooms.find(r => r.id === camera.roomId);
        let shot = CameraShotUtility.getCameraShot(state.settings.project_id, room, camera, state.categories);
        state.printingShots.push(shot);
      });

    },
    cancelRuleFulfillment: (state, action) => {
      state.popupState = PopupState.None;
      state.addedCategoriesFromRule = [];
      state.removedCategoriesFromRule = [];
    },
    applyRuleFulfillment: (state, action) => {
      state.addedCategoriesFromRule.map(addedCategory => {
        state.categories.forEach(category => {
          if(category.id === addedCategory.id)
          {
            category.selected_feature = addedCategory.selected_feature;
          }
        });
      });

      state.addedCategoriesFromRule = [];
      state.removedCategoriesFromRule = [];
    },
    setUnfoldedCategory: (state, action) => {
      state.currentUnfoldedCategory = action.payload.id;
      state.currentUnfoldOffset = action.payload.offset;
    },
    saveConfiguration: (state, action) => {
      state.isSavingConfiguration = true;
    },
    saveConfigurationComplete: (state, action) => {
      state.isSavingConfiguration = false;
    },
    downloadPdf: (state, action) => {
      state.isDownloadingPdf = true;
    },
    downloadPdfComplete: (state, action) => {
      state.isDownloadingPdf = false;
    },
    requestBudget: (state, action) => {
      state.requestingBudgetUnitId = state.currentUnit.id;
      state.isRequestingBudget = true;
    },
    setCurrentConfigCode: (state, action) => {
      state.currentConfigCode = action.payload;
    },
    setConfigurationChanged: (state, action) => {
      state.configurationChanged = action.payload;
    },
    setUser: (state, action) => {
      if(!IsNullOrUndefined(action.payload)) {
        state.user = action.payload;

        if (action.payload.status === UserStatus.LoggedOut) {
          state.applicationState = ApplicationState.UserLoggedOut;
        } else if (action.payload.status === UserStatus.SetPasswordRequired) {
          state.applicationState = ApplicationState.UserSetPasswordRequired;
        } else if (action.payload.status === UserStatus.LoggedIn) {
          if(state.isRequestingBudget) {
            state.isRequestingBudget = false;
            state.currentUnit = action.payload.units.find(u => u.id === state.requestingBudgetUnitId);
            state.requestingBudgetUnitId = null;
          } else if (action.payload.units.length > 1) {
            state.applicationState = ApplicationState.UserSelectFromMultipleUnitsRequired;
          } else {
            state.currentUnit = action.payload.units.length > 0 ? action.payload.units[0] : null;
            state.applicationState = ApplicationState.UserLoggedIn;
          }
        }
        console.log("user: " + action.payload);
      }
    },
    setUserLoginFailed: (state, action) => {
      state.userLoginError = action.payload;
      state.applicationState = ApplicationState.UserLoginFailed;
    },
    setCurrentUnit: (state, action) => {
      state.currentUnit = action.payload;
      state.applicationState = ApplicationState.UserLoggedIn;
    },
    setUnitUsers: (state, action) => {
      state.unitUsers = action.payload;
    },
    changeRegisterPopupOpened: (state, action) => {
      state.isRegisterPopupOpened = action.payload;
    },
    setSnackbarMessage: (state, action) => {
      state.snackbarMessage = action.payload;
    },
    changeLanguage: (state, action) => {
      state.language = action.payload;

      switch (action.payload){
        case Language.es:
          state.currentTranslations = {};
          state.currentLanguage = Language.es;
          break;
        case Language.en:
          if (!IsNullOrEmpty(state.translations)) {
            const foundLanguage = state.translations.find(t => t.language === Language.en);
            if (!IsNullOrUndefined(foundLanguage)) {
              const translationsForUI = TranslationUtility.GetTranslationsForUI(Language.en);
              state.currentTranslations = Object.assign({}, foundLanguage.translations, translationsForUI);
              state.currentLanguage = Language.en;
            }
          } else {
            state.currentTranslations = {};
            state.currentLanguage = Language.es;
          }
          break;
        case Language.ca:
          if (!IsNullOrEmpty(state.translations)) {
            const foundLanguage = state.translations.find(t => t.language === Language.ca);
            if (!IsNullOrUndefined(foundLanguage)) {
              const translationsForUI = TranslationUtility.GetTranslationsForUI(Language.ca);
              state.currentTranslations = Object.assign({}, foundLanguage.translations, translationsForUI);
              state.currentLanguage = Language.ca;
            }
          } else {
            state.currentTranslations = {};
            state.currentLanguage = Language.es;
          }
          break;
        default:
          state.currentTranslations = {};
          state.currentLanguage = Language.es;
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setPromotionId,
  initializePromotion,
  resetPromotion,
  setApplicationState,
  setPopupState,
  applyFeatureSelection,
  changeRoom,
  changeFeature,
  updateTotalPrice,
  changeCategoriesUnfolded,
  changeIsDescriptionOpened,
  changeIsMainMenuOpened,
  changeIsSummaryOpened,
  changeIsCategoryFocusEnabled,
  updateFocusedCategory,
  updatePreviousShot,
  updateCurrentCameraShot,
  initializeSummaryCameras,
  initializeSections,
  updateSections,
  initializeFeatureSelectionForPrinting,
  initializeSectionsForPrinting,
  cancelRuleFulfillment,
  applyRuleFulfillment,
  setUnfoldedCategory,
  saveConfiguration,
  saveConfigurationComplete,
  downloadPdf,
  downloadPdfComplete,
  requestBudget,
  requestBudgetComplete,
  setCurrentConfigCode,
  setConfigurationChanged,
  setUser,
  setUserLoginFailed,
  setCurrentUnit,
  setUnitUsers,
  setSnackbarMessage,
  changeLanguage,
} = configuratorSlice.actions;

export default configuratorSlice.reducer;
